// Possibly reusable Promise cancelation util.

export class CancelationError extends Error {
  constructor(message = "Promise was cancelled") {
    super(message);
    this.name = "CancelationError";
  }
}

/**
 * Wraps the delegate in a promise that can be canceled. Canceled promises will
 * reject, so add a catch block with `allowCancellations at the end of your
 * promise if you want to just discard canceled tasks.
 *
 * Especially useful when paired with `useEffect` since canceling makes sure you
 * won't accidentally update your component after it's unmounted.
 *
 * example:
 *   useEffect(() => {
 *     const { promise, cancel } = cancelable(fetchSomeData());
 *     promise
 *       .then(processThatData)
 *       .catch(allowCancelations);
 *
 *     return cancel;
 *   }, []);
 */
export function cancelable<T>(delegate: Promise<T>): {
  promise: Promise<T>;
  cancel: () => void;
} {
  const controller = new AbortController();
  return {
    promise: new Promise((resolve, reject) => {
      controller.signal.addEventListener("abort", () =>
        reject(new CancelationError()),
      );
      delegate.then(resolve).catch(reject);
    }),
    cancel: () => controller.abort(),
  };
}

/** Catch can discard promises canceled via the cancelable function. */
export function allowCancellations(error: unknown) {
  if (error instanceof CancelationError) return;
  throw error;
}
